import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Badge } from '../../design-system';
import { formatDate, formatCurrency } from '../../utils/formatters.js';
import { useTransferCandidates } from '../../api/transfer_review.js';
import { useGetCaseS3ObjectUrl } from '../../api/caseS3object.js';
import PdfViewer from './PDFViewer.js';

// Review the AI's candidate set for one transfer leg and re-point the match to a
// different candidate — with both statement PDFs shown side by side, highlighted
// to the line, so the reviewer can look at the source to decide.
// See .claude/references/transfer-review-manual-adjust.md (ADR-001).

// Match strength comes from the server's `confidence` tier, NOT from `q_signal`.
// q_signal only measures whether a description embeds the counterpart's account
// number — rare enough in real statements that it read 0 (and rendered "weak")
// for essentially every candidate, making the label a constant. See
// compute_match_confidence in the shared layer's transfers.py (Trello rKNmPcDb).
const CONFIDENCE_LABEL = {
  STRONG: 'Strong match',
  LIKELY: 'Likely match',
  AMBIGUOUS: 'Ambiguous',
  WEAK: 'Weak match',
};

const CONFIDENCE_VARIANT = {
  STRONG: 'success',
  LIKELY: 'info',
  AMBIGUOUS: 'warning',
  WEAK: 'default',
};

// The evidence behind the tier, so the reviewer can see WHY rather than trust an
// adjective. Falls back to bare date proximity when the API predates
// `confidence_reasons` (rolling deploy) — showing no tier beats showing a wrong one.
function confidenceText(candidate) {
  const reasons = candidate.confidence_reasons;
  if (reasons && reasons.length > 0) return reasons.join(', ');
  const days = candidate.days_diff;
  return days === 0 || days == null
    ? 'same day'
    : `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} apart`;
}

// Compact summary of a transaction leg (anchor or candidate).
function LegSummary({ date, account, description, subAccount }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
          {date ? formatDate(date) : ''}
        </Typography>
        <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)' }}>
          {account}
        </Typography>
        {subAccount != null && String(subAccount).trim() !== '' && (
          <Badge variant="info">Sub-acct {subAccount}</Badge>
        )}
      </Box>
      <Typography
        sx={{ fontSize: 'var(--text-base)', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        title={description}
      >
        {description}
      </Typography>
    </Box>
  );
}

function CenteredPane({ children, error }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 'var(--space-4)' }}>
      <Typography sx={{ fontSize: 'var(--text-sm)', color: error ? 'var(--color-error-600)' : 'var(--text-tertiary)', textAlign: 'center' }}>
        {children}
      </Typography>
    </Box>
  );
}

// One statement-PDF pane (subject or candidate). Signs the URL from the leg's
// S3 fields and highlights the line via bbox (fuzzy transactionSearch fallback).
function TransferLegPdf({ label, leg, scrollNonce }) {
  const bucketName = leg?.bucketName || null;
  const objectKey = leg?.objectKey || null;
  const { data: url, error, isLoading } = useGetCaseS3ObjectUrl(bucketName, objectKey, !!objectKey);

  let body;
  if (!leg) {
    body = <CenteredPane>Select a candidate to compare its statement.</CenteredPane>;
  } else if (!objectKey) {
    body = <CenteredPane>No source statement is available for this transaction.</CenteredPane>;
  } else if (isLoading) {
    body = <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={26} /></Box>;
  } else if (error) {
    body = <CenteredPane error>Could not load this statement.</CenteredPane>;
  } else if (url) {
    body = (
      <PdfViewer
        url={url}
        displayPage={Number(leg.page) || 1}
        boundingBox={leg.bbox || null}
        transactionSearch={{ descr: leg.description || null, amount: leg.amount ?? null, posting_date: leg.posting_date || null }}
        documentName={leg.documentName || null}
        scrollHighlightIntoView
        scrollHighlightNonce={scrollNonce}
        navigationKey={scrollNonce}
      />
    );
  } else {
    body = <CenteredPane>No statement to display.</CenteredPane>;
  }

  return (
    <Box sx={{
      flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
      border: 'var(--border-light)', borderRadius: 'var(--radius-md)', overflow: 'hidden',
      background: 'var(--surface-primary)',
    }}>
      <Box sx={{ px: 'var(--space-3)', py: 'var(--space-2)', borderBottom: 'var(--border-light)', background: 'var(--surface-secondary)', minWidth: 0 }}>
        <Typography sx={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label}
        </Typography>
        {leg?.account && (
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={leg.account}>
            {leg.account}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{body}</Box>
    </Box>
  );
}

const TransferMatchDialog = ({
  open,
  onClose,
  anchorTransaction,
  currentPartnerId,
  currentDisposition,
  onSelectMatch,
  onConfirm,
  onReject,
}) => {
  const anchorId = anchorTransaction?.transaction?.id;
  const { data, isLoading, error } = useTransferCandidates(anchorId, open);
  const candidates = data?.candidates || [];

  const anchorAccount = anchorTransaction?.bankAccount?._displayName || 'Unknown account';
  const anchorAmount = anchorTransaction?.transaction?.amount ?? 0;
  const hasCurrentMatch = Boolean(currentPartnerId);
  const alreadyConfirmed = currentDisposition === 'CONFIRMED';

  // Which candidate's statement is shown on the right (focus for viewing —
  // distinct from committing via "Use this match"). Defaults to the current match.
  const [focusedId, setFocusedId] = useState(null);
  // Bump to recenter a pane's PDF on its highlight when its row is clicked.
  const [subjectScrollNonce, setSubjectScrollNonce] = useState(0);
  const [candidateScrollNonce, setCandidateScrollNonce] = useState(0);
  useEffect(() => {
    // Re-seed the focus each time the dialog opens for a (possibly different) leg.
    if (open) setFocusedId(null);
  }, [open, anchorId]);

  const focusCandidate = (txnId) => {
    setFocusedId(txnId);
    setCandidateScrollNonce((n) => n + 1); // recenter even if it's already focused
  };

  // The server ranks purely by q. Presentation pins the current match to the
  // top of that ranking: this list is capped at 30vh (~4 rows), so an incumbent
  // that ranks low would open below the fold and the reviewer would be reading
  // alternatives without seeing what they are alternatives TO. Order among the
  // rest is left exactly as the server ranked it, and the pinned row keeps its
  // "Current match" badge and "Selected" cell, so first never reads as best.
  const orderedCandidates = useMemo(() => {
    const i = currentPartnerId ? candidates.findIndex((c) => c.txn_id === currentPartnerId) : -1;
    if (i <= 0) return candidates;  // absent, or already first
    const rest = candidates.slice();
    const [current] = rest.splice(i, 1);
    return [current, ...rest];
  }, [candidates, currentPartnerId]);

  const effectiveFocusedId = focusedId
    || currentPartnerId
    || (orderedCandidates.length ? orderedCandidates[0].txn_id : null);
  const focusedCandidate = candidates.find((c) => c.txn_id === effectiveFocusedId) || null;

  const subjectLeg = anchorTransaction ? {
    bucketName: anchorTransaction.transaction?._awsBucketName,
    objectKey: anchorTransaction.transaction?._awsUploadedDocumentKey || anchorTransaction.transaction?._awsObjectKey,
    bbox: anchorTransaction.transaction?.bbox,
    page: anchorTransaction.transaction?.page_number || anchorTransaction.transaction?.start_page,
    description: anchorTransaction.transaction?.description,
    amount: anchorTransaction.transaction?.amount,
    posting_date: anchorTransaction.transaction?.posting_date,
    account: anchorAccount,
  } : null;

  const candidateLeg = focusedCandidate ? {
    bucketName: focusedCandidate.bucket_name,
    objectKey: focusedCandidate.object_key,
    bbox: focusedCandidate.bbox,
    page: focusedCandidate.page_number,
    description: focusedCandidate.description,
    amount: focusedCandidate.amount,
    posting_date: focusedCandidate.posting_date,
    account: focusedCandidate.account_display,
  } : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ fontFamily: 'var(--font-display)' }}>
        Review transfer match
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* TOP: subject + candidate list (capped, scrollable) — kept compact so
            the two statement PDFs below get the majority of the height. */}
        <Box sx={{ p: 'var(--space-4)', overflowY: 'auto', maxHeight: '30vh', flexShrink: 0, borderBottom: 'var(--border-light)' }}>
          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 'var(--space-1)' }}>
            Matching this transfer
          </Typography>
          <Box
            onClick={() => setSubjectScrollNonce((n) => n + 1)}
            title="Scroll the subject statement to this line"
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', cursor: 'pointer',
              p: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: 'var(--border-light)', background: 'var(--surface-secondary)',
              mb: 'var(--space-4)', '&:hover': { background: 'var(--surface-tertiary)' },
            }}>
            <LegSummary date={anchorTransaction?.transaction?.posting_date} account={anchorAccount}
              subAccount={anchorTransaction?.bankAccount?.sub_account_number}
              description={anchorTransaction?.transaction?.description || 'No description'} />
            <Typography sx={{ fontWeight: 'var(--font-semibold)', color: anchorAmount < 0 ? 'var(--color-error-600)' : 'var(--color-success-600)', whiteSpace: 'nowrap' }}>
              {formatCurrency(anchorAmount)}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-primary)', mb: 'var(--space-1)' }}>
            Possible matches{candidates.length ? ` (${candidates.length})` : ''}
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', mb: 'var(--space-2)' }}>
            Select a candidate to view its statement on the right.
          </Typography>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 'var(--space-6)' }}><CircularProgress size={28} /></Box>
          )}
          {error && (
            <Typography sx={{ color: 'var(--color-error-600)', fontSize: 'var(--text-sm)' }}>
              Could not load candidate matches. Please try again.
            </Typography>
          )}
          {!isLoading && !error && candidates.length === 0 && (
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
              No other candidate matches were found for this transfer within the matching window.
            </Typography>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {orderedCandidates.map((c) => {
              const isCurrent = c.txn_id === currentPartnerId;
              const isFocused = c.txn_id === effectiveFocusedId;
              const pairedElsewhere = !isCurrent && c.current_partner_id && c.current_partner_id !== anchorId;
              const lockedElsewhere = pairedElsewhere && c.current_disposition === 'CONFIRMED';
              return (
                <Box
                  key={c.txn_id}
                  onClick={() => focusCandidate(c.txn_id)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)',
                    p: 'var(--space-3)', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                    border: isFocused ? '2px solid var(--color-navy-400)' : isCurrent ? '2px solid var(--color-accent-400)' : 'var(--border-light)',
                    background: isCurrent ? 'var(--color-accent-50)' : 'var(--surface-primary)',
                    '&:hover': { background: isCurrent ? 'var(--color-accent-50)' : 'var(--surface-tertiary)' },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <LegSummary date={c.posting_date} account={c.account_display || 'Account'}
                      subAccount={c.sub_account_number} description={c.description || 'No description'} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', mt: 'var(--space-1)', flexWrap: 'wrap' }}>
                      {CONFIDENCE_LABEL[c.confidence] && (
                        <Badge variant={CONFIDENCE_VARIANT[c.confidence] || 'default'}>
                          {CONFIDENCE_LABEL[c.confidence]}
                        </Badge>
                      )}
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{confidenceText(c)}</Typography>
                      {isCurrent && <Badge variant="accent">Current match</Badge>}
                      {isFocused && <Badge variant="info">Viewing</Badge>}
                      {c.suppressed && <Badge variant="error">Rejected</Badge>}
                      {lockedElsewhere && <Badge variant="default">Confirmed elsewhere</Badge>}
                      {pairedElsewhere && !lockedElsewhere && <Badge variant="warning">Matched elsewhere</Badge>}
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 'var(--font-semibold)', color: c.amount < 0 ? 'var(--color-error-600)' : 'var(--color-success-600)', whiteSpace: 'nowrap' }}>
                    {formatCurrency(c.amount)}
                  </Typography>
                  <Box sx={{ flexShrink: 0, width: 132, textAlign: 'right' }}>
                    {isCurrent ? (
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>Selected</Typography>
                    ) : (
                      <Button
                        variant="outlined" size="small" disabled={lockedElsewhere}
                        onClick={(e) => { e.stopPropagation(); onSelectMatch(c); }}
                        title={lockedElsewhere ? 'This leg is confirmed to another transfer'
                          : c.suppressed ? 'Restore this previously-rejected match' : 'Use this as the match'}
                      >
                        {c.suppressed ? 'Restore' : 'Use this match'}
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* BOTTOM: the two statement PDFs, side by side */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 'var(--space-2)', p: 'var(--space-2)', background: 'var(--surface-tertiary)' }}>
          <TransferLegPdf label="Subject transfer" leg={subjectLeg} scrollNonce={subjectScrollNonce} />
          <TransferLegPdf label="Selected candidate" leg={candidateLeg} scrollNonce={candidateScrollNonce} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 'var(--space-4)' }}>
        <Button color="error" disabled={!hasCurrentMatch} onClick={() => onReject?.(anchorId, currentPartnerId)}>
          Unmatch this transfer
        </Button>
        <Box sx={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button onClick={onClose}>Close</Button>
          {hasCurrentMatch && !alreadyConfirmed && (
            <Button variant="contained" onClick={() => onConfirm?.(anchorId, currentPartnerId)}>
              Confirm match
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TransferMatchDialog;
